import * as Sentry from '@sentry/react';
import { scrubSensitiveUrl } from 'src/analytics/sanitizeSearch';
import { resolveTracesSampleRate } from 'src/sentrySampleRate';

/** NPT-2091 — keep the /claim link's token (it resolves to the traveler's email) out of Sentry. */
const scrubEventUrls = <T extends { request?: { url?: string }; breadcrumbs?: Array<{ data?: Record<string, unknown> }> }>(event: T): T => {
   if (event.request?.url) event.request.url = scrubSensitiveUrl(event.request.url) ?? undefined;
   event.breadcrumbs?.forEach(crumb => {
      if (!crumb.data) return;
      for (const key of ['url', 'from', 'to']) {
         const value = crumb.data[key];
         if (typeof value === 'string') crumb.data[key] = scrubSensitiveUrl(value);
      }
   });
   return event;
};

// Browser-side Sentry bootstrap. Called once at module load from
// src/Root.tsx (which only runs on the client because pages/index/
// +Page.tsx is client-only with ssr: false). Server-side Sentry init
// lives in src/sentryServer.ts and is preloaded via Node's --import
// flag — see src/server/instrument.ts.
//
// Sentry's hub model keeps the two SDKs coexisting cleanly: client
// errors flow through @sentry/react, server errors through @sentry/node,
// both into the same DSN and release tag.
export function initSentry() {
   if (typeof window === 'undefined') return;

   const isLocal = window.location.hostname === 'localhost';
   const dsn = import.meta.env.VITE_SENTRY_DSN;
   const mode = import.meta.env.MODE;
   const allowedModes = ['development', 'staging', 'production'];

   if (!dsn || isLocal || !allowedModes.includes(mode)) return;

   const release = `${import.meta.env.VITE_PACKAGE_NAME}@${import.meta.env.VITE_PACKAGE_VERSION}`;

   Sentry.init({
      dsn,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      release,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: resolveTracesSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE),
      // v11 defaults to span streaming ('stream'), which never calls
      // beforeSendTransaction. 'static' keeps transaction events, so the
      // NPT-2091 scrub below still runs on them.
      traceLifecycle: 'static',
      beforeSend: event => scrubEventUrls(event),
      beforeSendTransaction: event => scrubEventUrls(event),
      beforeBreadcrumb: breadcrumb => {
         if (breadcrumb.data) {
            for (const key of ['url', 'from', 'to']) {
               const value = breadcrumb.data[key];
               if (typeof value === 'string') breadcrumb.data[key] = scrubSensitiveUrl(value);
            }
         }
         return breadcrumb;
      },
      // Unactionable third-party/browser noise, triaged individually (Sentry
      // NPT-WEB-7J, 76, 7H, 7C, 7D, 63) — none have a first-party stack frame, so
      // there's no app code to fix. Ignoring keeps the issue stream signal-only.
      ignoreErrors: [
         // Google reCAPTCHA's own minified script throwing on internal state.
         "Cannot read properties of undefined (reading 'II')",
         // Hotjar's bundled script failing to read/write localStorage, usually
         // in Firefox private browsing or during pagehide teardown.
         'NS_ERROR_FAILURE',
         // Safari/iOS dropping its IndexedDB connection; self-resolves on reload.
         'Connection to Indexed Database server lost',
         // Known mapbox-gl-js bug: its session-telemetry request resolves after
         // map.remove() has already nulled out the callback it tries to invoke.
         'this.errorCb is not a function',
         // Vike's hydration-pristine-URL assertion — a benign race that can fire
         // if the URL changes a tick before hydration finishes; the page recovers.
         '[vike][Wrong Usage]',
         // The browser cancelling an in-flight XHR when the user navigates away
         // mid-request — expected, not a failed request our code needs to handle.
         'Request aborted',
      ],
   });
}
